import React from 'react';
import { Paper, Typography, useTheme, CircularProgress, Alert } from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import dayjs from 'dayjs';
import { LineChart } from '@personal-dashboard/ui';

const GET_DAILIES_STATS = gql`
  query GetDailiesStats($dateRange: DateRange!) {
    getDays(dateRange: $dateRange) {
      id
      date
      completedDailies {
        id
      }
    }
    getDailies(activeOnly: true) {
      id
    }
  }
`;

const DailiesTrendChart: React.FC<{ daysCount: number }> = ({ daysCount }) => {
  const theme = useTheme();
  
  const dateRange = {
    start: dayjs().subtract(daysCount, 'day').startOf('day').toISOString(),
    end: dayjs().endOf('day').toISOString(),
  };

  const { data, loading, error } = useQuery(GET_DAILIES_STATS, {
    variables: { dateRange },
    fetchPolicy: 'cache-and-network'
  });

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load dailies: {error.message}</Alert>;

  const dayData = data?.getDays || [];
  const totalActiveDailies = data?.getDailies?.length || 0;
  if (totalActiveDailies === 0) return <Typography>No active dailies configured.</Typography>;

  // Generate continuous gap-free date array for the selected range
  const weekDays = Array.from({ length: daysCount }, (_, i) => 
    dayjs().subtract(daysCount - 1 - i, 'day').startOf('day')
  );

  const dates = weekDays.map(d => d.format('DD/MM'));

  // Map database metrics into the continuous date list, defaulting to 0 for missing dates
  const completionRates = weekDays.map(day => {
    const dateStr = day.format('YYYY-MM-DD');
    const match = dayData.find((d: any) => {
      const dDate = dayjs(isNaN(Number(d.date)) ? d.date : Number(d.date));
      return dDate.format('YYYY-MM-DD') === dateStr;
    });

    if (!match || totalActiveDailies === 0) return 0;
    return Math.round((match.completedDailies.length / totalActiveDailies) * 100);
  });

  const series = [
    {
      name: 'Completion',
      data: completionRates,
      itemStyle: { color: theme.palette.success.main },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: theme.palette.success.main + '44' },
            { offset: 1, color: theme.palette.success.main + '00' }
          ]
        }
      }
    }
  ];

  return (
    <Paper sx={{ p: 3, height: 400, borderRadius: 2 }}>
      <LineChart
        title="Habit Consistency (%)"
        categories={dates}
        series={series}
        yAxisFormatter="{value}%"
        tooltipFormatter="{b}: {c}%"
        height="100%"
      />
    </Paper>
  );
};

export default DailiesTrendChart;
